import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Rocket, QrCode, Smartphone, Building2, ShieldAlert, Lock, Router, Fingerprint,
  CameraOff, KeyRound, LayoutGrid, Download, Settings2, MonitorSmartphone, EyeOff, MapPin,
  ScreenShare, RefreshCw, BatteryCharging,
} from "lucide-react";
import SEO from "@/components/SEO";

const CATEGORIES = [
  {
    title: "Deployment & enrollment",
    icon: <Rocket size={22} />,
    items: [
      { icon: <QrCode size={16} />, text: "Zero-touch enrollment for automated out-of-the-box setup" },
      { icon: <QrCode size={16} />, text: "Bulk provisioning via QR codes, NFC, or enrollment tokens" },
      { icon: <Smartphone size={16} />, text: "Dedicated work profiles for personal devices (BYOD)" },
      { icon: <Building2 size={16} />, text: "Corporate-owned configurations (COBO / COPE)" },
    ],
  },
  {
    title: "Device security & compliance",
    icon: <ShieldAlert size={22} />,
    items: [
      { icon: <Lock size={16} />, text: "Remote factory reset and device locking" },
      { icon: <Router size={16} />, text: "Custom APN loading and cellular configuration" },
      { icon: <Fingerprint size={16} />, text: "Enforced password, PIN, and biometric policies" },
      { icon: <CameraOff size={16} />, text: "Disabling of hardware like cameras, Bluetooth, and USB ports" },
      { icon: <ShieldAlert size={16} />, text: "Data loss prevention to stop cross-profile copy-pasting" },
      { icon: <KeyRound size={16} />, text: "Factory Reset Protection overrides for stolen devices" },
    ],
  },
  {
    title: "Application management",
    icon: <LayoutGrid size={22} />,
    items: [
      { icon: <LayoutGrid size={16} />, text: "Custom enterprise app catalog via Managed Google Play" },
      { icon: <Download size={16} />, text: "Silent app installation, updates, and uninstallation" },
      { icon: <Settings2 size={16} />, text: "Remote pre-configuration of app settings" },
      { icon: <KeyRound size={16} />, text: "Automated control over app runtime permissions" },
    ],
  },
  {
    title: "Device lockdown & kiosk mode",
    icon: <MonitorSmartphone size={22} />,
    items: [
      { icon: <MonitorSmartphone size={16} />, text: "Single-app or multi-app kiosk lockdown" },
      { icon: <EyeOff size={16} />, text: "Disabling of system status bars and navigation buttons" },
      { icon: <Settings2 size={16} />, text: "Remotely restricted settings menus" },
    ],
  },
  {
    title: "Monitoring & support",
    icon: <MapPin size={22} />,
    items: [
      { icon: <MapPin size={16} />, text: "Real-time GPS location tracking and geofencing" },
      { icon: <ScreenShare size={16} />, text: "Remote screen viewing and control for troubleshooting" },
      { icon: <RefreshCw size={16} />, text: "Scheduled, paused, or forced Android OS updates" },
      { icon: <BatteryCharging size={16} />, text: "Device hardware and battery health auditing" },
    ],
  },
];

const AndroidMDM = () => (
  <div data-testid="android-mdm-page">
    <SEO
      path="/solutions/android-mdm"
      title="Android MDM — Device Management in Your Portal"
      description="Native Android Enterprise device management built into the PTT NZ portal — zero-touch enrollment, remote lock & wipe, custom APN loading, kiosk mode."
      keywords="Android MDM New Zealand, mobile device management Australia, Android Enterprise management NZ, zero touch enrollment Android, kiosk mode Android business, remote device lock wipe NZ, managed Google Play business, Android fleet management NZ AU, custom APN device management, BYOD COBO COPE New Zealand"
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Android Mobile Device Management (MDM)",
        provider: { "@type": "Organization", name: "PTT NZ Limited" },
        areaServed: ["New Zealand", "Australia"],
        description: "Native Android Enterprise mobile device management built into the PTT NZ customer portal — enrollment, security, application management, kiosk lockdown and monitoring for Android device fleets.",
      }}
    />

    {/* HERO */}
    <section className="bg-[var(--ptt-paper)] border-b border-[var(--ptt-line)]">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-16 pb-14">
        <div className="eyebrow">Native platform capability</div>
        <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 leading-[1.02] tracking-tight max-w-3xl">
          Android MDM, <span className="text-[var(--ptt-teal-deep)]">built right into your portal.</span>
        </h1>
        <p className="text-[var(--ptt-muted)] mt-5 max-w-2xl leading-relaxed">
          Every PTT NZ account already includes native Android Enterprise device management — no extra software to install, no separate login to remember. Enroll, secure, lock down and monitor your entire Android fleet from the same portal you use for SIMs, data and billing.
        </p>
        <div className="mt-7 flex gap-3 flex-wrap">
          <Link to="/contact?interest=Android%20MDM" data-testid="mdm-hero-cta-contact" className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center gap-2">Talk to us about MDM <ArrowRight size={14} /></Link>
        </div>
      </div>
    </section>

    {/* PORTAL SCREENSHOT */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
      <div className="eyebrow">Straight from the portal</div>
      <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">One dashboard for your whole Android fleet.</h2>
      <div className="mt-10 border border-[var(--ptt-line)] bg-white p-1.5 shadow-[6px_6px_0_var(--ptt-ink)]" data-testid="mdm-portal-shot">
        <img
          src="/assets/portal-mdm-android.webp"
          alt="PTT NZ portal MDM (Android Devices) view showing Android Enterprise connection, network policies and enrolled devices"
          className="w-full h-auto block"
          loading="lazy"
        />
      </div>
    </section>

    {/* FEATURE CATEGORIES */}
    <section className="bg-[var(--ptt-ink)] text-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
        <div className="eyebrow text-white/50">What's included</div>
        <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Five categories of control, one native platform.</h2>
        <div className="grid md:grid-cols-2 gap-5 mt-10" data-testid="mdm-category-grid">
          {CATEGORIES.map((cat) => (
            <div key={cat.title} className="border border-white/15 p-6" data-testid={`mdm-category-${cat.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 grid place-items-center bg-white/10 text-[var(--ptt-teal)] shrink-0">{cat.icon}</div>
                <h3 className="font-display text-lg font-bold">{cat.title}</h3>
              </div>
              <ul className="mt-4 space-y-2.5">
                {cat.items.map((it) => (
                  <li key={it.text} className="flex items-start gap-2.5 text-sm text-white/80">
                    <span className="text-[var(--ptt-teal)] shrink-0 mt-0.5">{it.icon}</span>{it.text}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* FINAL CTA */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 mb-8">
      <div className="relative border border-[var(--ptt-ink)] p-8 lg:p-14 bg-white overflow-hidden">
        <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-[var(--ptt-teal)]/20 blur-3xl" />
        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="eyebrow">Talk to us</div>
            <h3 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-tight">Bring your Android fleet under control.</h3>
            <p className="text-[var(--ptt-muted)] mt-4 max-w-2xl">Whether it&apos;s a handful of rugged handhelds or a nationwide fleet, MDM is included in your PTT NZ portal — no extra licence, no extra login.</p>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Link to="/contact?interest=Android%20MDM" data-testid="mdm-final-cta-contact" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2 w-full sm:w-auto justify-center">Talk to us about MDM <ArrowRight size={16} /></Link>
            <Link to="/solutions" data-testid="mdm-final-cta-solutions" className="ghost-button px-6 py-3.5 text-sm font-semibold inline-flex items-center gap-2 w-full sm:w-auto justify-center">Back to Solutions</Link>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default AndroidMDM;
